import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import colors from "../../../../../Constants/colors";

const StackedBarGraph = ({ data }) => {
  //
  // const testData = [
  //   { botName: "AlphaBot", cost1: 210, cost2: 350, cost3: 120 },
  //   { botName: "BetaBot", cost1: 275, cost2: 315, cost3: 290 },
  //   { botName: "GammaBot", cost1: 480, cost2: 520, cost3: 600 },
  //   { botName: "DeltaBot", cost1: 190, cost2: 410, cost3: 130 },
  //   { botName: "EpsilonBot", cost1: 240, cost2: 330, cost3: 370 },
  //   { botName: "ZetaBot", cost1: 460, cost2: 495, cost3: 580 },
  // ];
  // Utility function to generate random colors
  const getRandomColor = () => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  const getColor = (() => {
    const colors = [
      "#10A37F",
      "#6CD468",
      "#715FDE",
      "#DD5CE5",
      "#E63946",
      "#FFB703",
      "#F77F00",
      "#00BFFF",
      "#3A86FF",
      "#FF006E",
    ];

    let index = 0;

    return () => {
      const color = colors[index];
      index = (index + 1) % colors.length; // Move to the next color and wrap around if necessary
      return color;
    };
  })();

  const generateBars = (data) => {
    const keys = Object.keys(data?.[0]).filter((key) => key !== "botName");
    return keys.map((key, index) => (
      <Bar
        key={index}
        dataKey={key}
        stackId="a"
        fill={getColor()}
        barSize={15}
      />
    ));
  };

  const formatToScientific = (value) => {
    // if (value === 0) return "0";
    // const exponent = Math.floor(Math.log10(Math.abs(value)));
    // const mantissa = (value / Math.pow(10, exponent)).toFixed(3);
    // return `$${mantissa} × 10^${exponent}`;

    if (value === 0) return value; // Return 0 as is

    const exponent = Math.floor(Math.log10(Math.abs(value)));
    const mantissa = (value / Math.pow(10, exponent)).toFixed(2); // Mantissa with 2 decimal places

    // If the exponent is between -2 and 2 (inclusive), return the rounded value with 2 decimal places
    if (exponent >= -2 && exponent <= 2) {
      return Number(value).toFixed(2);
    }

    // Otherwise, return the mantissa and exponent in scientific notation
    return (
      <>
        {mantissa} &times; 10<sup>{exponent}</sup>
      </>
    );
  };

  const formatToScientificForAxis = (value) => {
    if (value === 0) return value.toString(); // Return 0 as a string

    const exponent = Math.floor(Math.log10(Math.abs(value)));
    const mantissa = (value / Math.pow(10, exponent)).toFixed(2); // Mantissa with 2 decimal places

    // If the exponent is between -2 and 2 (inclusive), return the rounded value as a string
    if (exponent >= -2 && exponent <= 2) {
      return Number(value).toFixed(2).toString();
    }

    // Return the mantissa and exponent as a string in scientific notation
    return `${mantissa} × 10^${exponent}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.6)", // More opaque background
            padding: "10px",
            borderRadius: "8px",
            color: "#fff",
          }}
        >
          <p className="label">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} style={{ color: entry.color }}>
              {`${entry.name}: ${formatToScientificForAxis(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomAxisTick = ({ x, y, payload }) => {
    const words = payload.value.split(" ");
    const maxWidth = 50; // Maximum width before wrapping to the next line
    let lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if (currentLine.length + word.length < maxWidth) {
        currentLine += `${word} `;
      } else {
        lines.push(currentLine);
        currentLine = `${word} `;
      }
    });

    lines.push(currentLine); // Add the last line

    return (
      <g transform={`translate(${x},${y}) rotate(-15)`}>
        {lines.map((line, index) => (
          <text
            key={index}
            x={0}
            y={index * 12}
            textAnchor="end"
            fill={colors.extremeWhite}
            fontSize={12}
          >
            {line.trim()}
          </text>
        ))}
      </g>
    );
  };

  return (
    <ResponsiveContainer
      width="100%"
      height={400}
      style={{ borderBottom: "2px solid gray", marginBottom: "32px" }}
    >
      <BarChart
        data={data}
        margin={{
          top: 20,
          right: 20,
          left: 10,
          bottom: 50,
        }}
      >
        {/* <CartesianGrid strokeDasharray="1 1" /> */}
        <XAxis
          dataKey="botName"
          tick={<CustomAxisTick />}
          angle={-45}
          textAnchor="end"
          tickMargin={10}
        />
        <YAxis
          tickFormatter={formatToScientificForAxis}
          tick={{ fontSize: 12, fill: colors.extremeWhite }}
        />
        <Tooltip content={<CustomTooltip />} />
        {/* <Legend
          layout="vertical"
          align="left"
          verticalAlign="middle"
          wrapperStyle={{
            fontSize: "12px",
          }}
        /> */}
        {generateBars(data)}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default StackedBarGraph;
